import { useEffect, useState } from "react";
import { ApiClientError } from "../../lib/api/client";
import { uploadFile } from "../../lib/api/files";
import {
  useSubmitExpenseMutation,
  useUpdateExpenseMutation,
  type ExpenseDto,
} from "../../lib/api/transactions";
import { formatMoney } from "../../lib/format/money";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { FormField, FormGrid } from "../../components/ui/FormField";
import { AllocationLinesEditor } from "./AllocationLinesEditor";
import {
  type AllocationLineForm,
  sumAllocationAmounts,
} from "./transaction-utils";

interface EditExpenseModalProps {
  open: boolean;
  onClose: () => void;
  expense: ExpenseDto;
  units: { id: string; name: string; code: string }[];
  funds: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  onSaved: () => void;
}

function toLineForm(expense: ExpenseDto): AllocationLineForm[] {
  return expense.lines.map((line) => ({
    unitId: line.unitId,
    fundId: line.fundId,
    categoryId: line.categoryId,
    amount: line.amount,
    costType: line.costType,
  }));
}

export function EditExpenseModal({
  open,
  onClose,
  expense,
  units,
  funds,
  categories,
  onSaved,
}: EditExpenseModalProps) {
  const updateExpense = useUpdateExpenseMutation();
  const submitExpense = useSubmitExpenseMutation();

  const [payee, setPayee] = useState(expense.payee);
  const [expenseDate, setExpenseDate] = useState(expense.expenseDate);
  const [description, setDescription] = useState(expense.description ?? "");
  const [missingEvidenceReason, setMissingEvidenceReason] = useState(
    expense.missingEvidenceReason ?? "",
  );
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [lines, setLines] = useState<AllocationLineForm[]>(() => toLineForm(expense));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPayee(expense.payee);
    setExpenseDate(expense.expenseDate);
    setDescription(expense.description ?? "");
    setMissingEvidenceReason(expense.missingEvidenceReason ?? "");
    setEvidenceFile(null);
    setLines(toLineForm(expense));
    setError(null);
  }, [expense, open]);

  const total = sumAllocationAmounts(lines);
  const hasEvidence = expense.files.length > 0;

  async function save(submitForApproval: boolean) {
    setError(null);

    const evidenceReason = missingEvidenceReason.trim();
    if (submitForApproval && !hasEvidence && !evidenceFile && !evidenceReason) {
      setError("Supporting evidence or a missing-evidence reason is required.");
      return;
    }

    try {
      await updateExpense.mutateAsync({
        id: expense.id,
        body: {
          payee,
          expenseDate,
          description: description || undefined,
          missingEvidenceReason:
            hasEvidence || evidenceFile ? undefined : evidenceReason || undefined,
          lines: lines.map((line) => ({
            unitId: line.unitId,
            fundId: line.fundId,
            categoryId: line.categoryId,
            costType: line.costType ?? "program_direct",
            amount: Number.parseFloat(line.amount).toFixed(2),
          })),
        },
      });

      if (evidenceFile) {
        await uploadFile({
          file: evidenceFile,
          entityType: "expense",
          entityId: expense.id,
        });
      }

      if (submitForApproval) {
        await submitExpense.mutateAsync({
          id: expense.id,
          missingEvidenceReason:
            hasEvidence || evidenceFile ? undefined : evidenceReason || undefined,
        });
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not save expense.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${expense.referenceNo}`}
      description="Update this draft or rejected expense, then save or resubmit for approval."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            disabled={updateExpense.isPending || submitExpense.isPending}
            onClick={() => void save(false)}
          >
            Save changes
          </Button>
          <Button
            disabled={updateExpense.isPending || submitExpense.isPending}
            onClick={() => void save(true)}
          >
            {updateExpense.isPending || submitExpense.isPending
              ? "Saving…"
              : "Save & submit"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? <ErrorBanner message={error} /> : null}
        {expense.rejectionReason ? (
          <p className="edlab-notice">Reviewer note: {expense.rejectionReason}</p>
        ) : null}
        <FormGrid>
          <FormField label="Payee / supplier">
            <input
              className="edlab-input"
              value={payee}
              onChange={(e) => {
                setPayee(e.target.value);
              }}
              required
            />
          </FormField>
          <FormField label="Expense date">
            <input
              type="date"
              className="edlab-input"
              value={expenseDate}
              onChange={(e) => {
                setExpenseDate(e.target.value);
              }}
            />
          </FormField>
          <FormField label="Description" fullWidth>
            <input
              className="edlab-input"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          </FormField>
          <FormField label="Supporting evidence (PDF, JPEG, PNG, WebP)" fullWidth>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              className="edlab-input"
              onChange={(e) => {
                setEvidenceFile(e.target.files?.[0] ?? null);
              }}
            />
            {hasEvidence ? (
              <p className="mt-1 text-xs text-edlab-muted">
                Current file: {expense.files.map((file) => file.originalName).join(", ")}
              </p>
            ) : null}
          </FormField>
          <FormField label="Missing evidence reason (if no file attached)" fullWidth>
            <input
              className="edlab-input"
              value={missingEvidenceReason}
              onChange={(e) => {
                setMissingEvidenceReason(e.target.value);
              }}
              placeholder="Explain why evidence is not attached yet"
            />
          </FormField>
        </FormGrid>
        <AllocationLinesEditor
          lines={lines}
          onChange={setLines}
          units={units}
          funds={funds}
          categories={categories}
          showCostType
        />
        <p className="text-sm font-medium text-edlab-ink">
          Total: {formatMoney(total.toFixed(2))}
        </p>
      </div>
    </Modal>
  );
}
